import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Hub, PlanType, User } from '@shared/models';
import { LoadState } from '../../loader-directive/loader.module';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { AnalyticsService, SessionData, chartColorPalette } from '@shared/services/analytics.service';
import { GoogleChartInterface } from 'ng2-google-charts';
import { CurrentHubService } from '@shared/services/currentHub.service';
import { InternalAnalyticsService } from '@shared/services/internalAnalytics.service';
import { LegendItem } from '../../misc/google-chart-legend';
import { courseLabel, StringCase } from '@shared/utils/wording.utils';

@Component({
  selector: 'hub-overview',
  templateUrl: './hub-overview.component.html',
  styleUrls: ['./hub-overview.component.scss']
})
export class HubOverviewComponent implements OnInit, OnChanges {
  @Input() hub: Hub;
  @Input() date_from: Date;
  @Input() date_to: Date;

  plan_end_date: Date;
  plan_type: PlanType;
  public visitorTrafficDataStatus = LoadState.pending;
  public visitorTrafficChart: GoogleChartInterface;
  public courseCreationsDataStatus = LoadState.pending;
  public courseCreationChart: GoogleChartInterface;
  public mostActiveCreatorsLoadStatus = LoadState.pending;
  public mostActiveCreators: User[];

  public sessionTrafficDataStatus = LoadState.pending;
  public sessionTrafficChart: GoogleChartInterface;
  public sessionTrafficChartLegend: LegendItem[];
  public totalRegisteredSessions: number;
  public totalGuestSessions: Number;

  public chartColor = '#247e8d';
  constructor(
    private _courseAPIService: CoursesAPIService,
    private _analyticsService: AnalyticsService,
    private _currentHubService: CurrentHubService,
    private _internalAnalyticsService: InternalAnalyticsService
  ) { }

  async ngOnInit(): Promise<void> {
    this.plan_end_date = this._currentHubService.planExpiration();
    this.plan_type = this._currentHubService.plan.type;
    this.updateCharts();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.date_from && !changes.date_from.isFirstChange() && changes.date_from.currentValue !== changes.date_from.previousValue)
      || (changes.date_to && !changes.date_to.isFirstChange() && changes.date_to.currentValue !== changes.date_to.previousValue)) {
      this.updateCharts();
    }
  }
  async updateCharts() {
    await this.updateVisitorTrafficChart();
    await this.updateCourseCreationTrafficChart();
    await this.updateMostActiveCreators();
    await this.updateSessionTrafficChart();

  }

  async updateVisitorTrafficChart() {
    this.visitorTrafficDataStatus = LoadState.loading;
    try {
      const stats = await this._analyticsService.getHubVisitorTraffic(this.hub.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
      if (!stats || stats.length === 0) {
        this.visitorTrafficDataStatus = LoadState.empty;
        return;
      }
      this.visitorTrafficDataStatus = LoadState.completed;
      const data = [[{ type: 'date' }, 'Users']];
      stats.map((stat: any) => {
        data.push([new Date(stat._id), stat.count]);
      });

      const dateFormat = 'YYYY-MM';
      this.visitorTrafficChart = {
        chartType: 'ColumnChart',
        dataTable: data,
        options: {
          legend: { position: 'none' },
          colors: [this.chartColor],
          chartArea: { width: '90%' },
          hAxis: {
            format: dateFormat
          }
        }
      };
    }
    catch (e) {
      this.visitorTrafficDataStatus = LoadState.error;
    }
  }

  async updateCourseCreationTrafficChart() {
    this.courseCreationsDataStatus = LoadState.loading;
    try {
      const stats = await this._courseAPIService.getHubCourseCreationsPerMonth(this.hub.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
      if (!stats || stats.length === 0) {
        this.courseCreationsDataStatus = LoadState.empty;
        return;
      }
      this.courseCreationsDataStatus = LoadState.completed;
      const data = [[{ type: 'date' }, `${courseLabel(this.hub.mode, true, StringCase.titlecase)} Created`]];
      stats.map((stat: any) => {
        data.push([new Date(stat._id), stat.count]);
      });

      const dateFormat = 'YYYY-MM';
      this.courseCreationChart = {
        chartType: 'ColumnChart',
        dataTable: data,
        options: {
          legend: { position: 'none' },
          colors: [this.chartColor],
          chartArea: { width: '90%' },
          hAxis: {
            format: dateFormat
          }
        }
      };
    }
    catch (e) {
      this.courseCreationsDataStatus = LoadState.error;
    }
  }
  async updateMostActiveCreators(): Promise<void> {
    this.mostActiveCreatorsLoadStatus = LoadState.loading;
    try {
      this.mostActiveCreators = await this._courseAPIService.getHubMostActiveCreators(this.hub.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
      this.mostActiveCreatorsLoadStatus = LoadState.completed;
    }
    catch (e) {
      this.mostActiveCreatorsLoadStatus = LoadState.error;
    }
  }
  async updateSessionTrafficChart() {
    this.sessionTrafficDataStatus = LoadState.loading;
    try {
      const stats: any[] = await this._analyticsService.getHubSessionTraffic(this.hub.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
      if (!stats || stats.length === 0) {
        this.sessionTrafficDataStatus = LoadState.empty;
        return;
      }
      this.sessionTrafficDataStatus = LoadState.completed;
      const data = [[{ type: 'date' }, 'Registered Sessions', 'Guest Sessions', { role: 'annotation' }]];
      this.totalRegisteredSessions = stats.reduce((sum, stat) => sum + stat.count, 0);
      this.totalGuestSessions = stats.reduce((sum, stat) => sum + stat.anonymousCount, 0);
      stats.map((stat: any) => {
        data.push([new Date(stat._id), stat.count, stat.anonymousCount, '']);
      });

      const dateFormat = 'YYYY-MM';
      this.sessionTrafficChart = {
        chartType: 'ColumnChart',
        dataTable: data,
        options: {
          legend: { position: 'none' },
          isStacked: true,
          colors: [chartColorPalette[0], chartColorPalette[1]],
          chartArea: { width: '90%' },
          hAxis: {
            format: dateFormat
          }
        }
      };
    }
    catch (e) {
      this.sessionTrafficDataStatus = LoadState.error;
    }
  }
  drawLegend(): void{
    this.sessionTrafficChartLegend = [
      {
        color: chartColorPalette[0],
        label: `Registered Sessions `,
        sublabel: `(total: ${this.totalRegisteredSessions})`
      },
      {
        color: chartColorPalette[1],
        label: `Guest Sessions `,
        sublabel: `(total: ${this.totalGuestSessions})`
      }
    ]
  }
  contactSales(): void {
    this._internalAnalyticsService.showNewMessage();
  }
}
